import { FC, useMemo } from 'react';
import { Box, Divider } from '@mui/material';
import ButtonGroup from '../../common/components/ButtonGroup';
import { Stack } from '@mui/system';
import SyntaxCodeDiffHighlighter from '../components/SyntaxCodeDiffHighlighter';
import Carousel from '../../elements/Carousel/Carousel';
import SlidingPanel from '../../elements/SlidingPanel/SlidingPanel';
import useOxTabs from '../../elements/OxTabs/hooks/useOxTabs';
import PaginationDots from '../../elements/PaginationDots/PaginationDots';
import { BiGitPullRequest } from 'react-icons/bi';
import { IssueDetails } from '../../issues/common/types/issues-types';
import { useSnapshot } from 'valtio';
import QuickFixStore from '../stores/quick-fix-store';
import { AggregationItem, Nullable } from 'common-types';
import FilesSelector from '../components/FilesSelector';
import FileToLargeToDisplay from '../components/FileToLargeToDisplay';

const FilesDiffContainer: FC<
  React.PropsWithChildren<FilesDiffContainerProps>
> = props => {
  const { viewFileFixes } = useSnapshot(QuickFixStore);
  const { onClose, onActionButtonClick } = props;
  const { handleTabChange, tabIndex } = useOxTabs();

  const fileNames = useMemo(
    () => viewFileFixes.map(f => f.filePath),
    [viewFileFixes],
  );

  const filesContainers = viewFileFixes.map((item, index) => {
    return (
      <SlidingPanel key={index} index={index} activeIndex={tabIndex}>
        <Stack spacing={1} direction='column' maxHeight={300} overflow='auto'>
          {item.diff ? (
            <Stack gap={1} flexDirection='row' position='relative'>
              <SyntaxCodeDiffHighlighter fileDiff={item.diff} />
            </Stack>
          ) : (
            <FileToLargeToDisplay />
          )}
          <Divider />
        </Stack>
      </SlidingPanel>
    );
  });

  return (
    <Box
      display='flex'
      flexDirection='column'
      justifyContent={'space-between'}
      height='100%'
      position='absolute'
      pb={2}
      pr={2}
      width='100%'>
      <Box display='flex' flexDirection='column'>
        <FilesSelector
          index={tabIndex}
          handleChange={handleTabChange}
          files={fileNames}
        />

        <Divider />
        {viewFileFixes.length > 1 ? (
          <>
            <Carousel tabIndex={tabIndex} onChange={handleTabChange}>
              {filesContainers}
            </Carousel>
            <PaginationDots activeIndex={tabIndex} dots={fileNames.length} />
          </>
        ) : (
          filesContainers
        )}
      </Box>
      <Box display='flex' alignItems='end'>
        <ButtonGroup
          startIcon={<BiGitPullRequest fontSize={12} />}
          onCancel={onClose}
          onClick={onActionButtonClick}
          buttonCancelLabel='close'
          buttonCancelTooltip='Close'
          buttonActionLabel='Open PR'
          buttonActionTooltip='Create pull request'
          buttonActionColor='primary'
          isActionDisabled={false}
        />
      </Box>
    </Box>
  );
};

interface FilesDiffContainerProps {
  selectedIssue: Nullable<IssueDetails>;
  selectedAggs: AggregationItem[];
  onClose: () => void;
  onActionButtonClick: () => void;
}

export default FilesDiffContainer;
